import React from 'react';
import { Box, Typography, Modal as LegoModal, Button, Loader } from '@lucid/lego';
import styles from './Modal.module.scss';

export type ModalVariant = 'success' | 'sending' | 'sent';

type CheckoutModalProps = {
  /** Test ID for the modal */
  dataTestId: string;
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /** Theme for the modal */
  theme?: 'light' | 'dark';
  /** Variant determines the modal's appearance and content */
  variant: ModalVariant;
  /** Custom message to display (for success variant) */
  message?: string;
  /** Whether to show loader (for sending variant) */
  showLoader?: boolean;
  /** i18n labels */
  labels?: Record<string, string>;
};

/**
 * Unified modal component for checkout operations
 * Handles success messages, sending states, and sent confirmations
 * @component
 * @param {CheckoutModalProps} props - Component props
 * @returns {JSX.Element} The CheckoutModal component
 * @example
 * ```tsx
 * // Success modal
 * <CheckoutModal
 *   dataTestId="save-quote-modal"
 *   isOpen={isModalOpen}
 *   onClose={handleClose}
 *   variant="success"
 *   message="Quote saved successfully"
 * />
 *
 * // Sending docusign modal
 * <CheckoutModal
 *   dataTestId="sending-docusign-modal"
 *   isOpen={isModalOpen}
 *   onClose={handleClose}
 *   variant="sending"
 *   showLoader={true}
 * />
 *
 * // Docusign sent modal
 * <CheckoutModal
 *   dataTestId="docusign-sent-modal"
 *   isOpen={isModalOpen}
 *   onClose={handleClose}
 *   variant="sent"
 * />
 * ```
 */
// CheckoutModal component
const CheckoutModal: React.FC<CheckoutModalProps> = ({
  dataTestId,
  isOpen,
  onClose,
  theme = 'light',
  variant,
  message,
  showLoader = false,
  labels,
}) => {
  // Default message with i18n fallback
  const defaultMessage =
    labels?.['checkout.modal.savedQuote.message'] ??
    'The quotation has been successfully saved to the prospect.';
  const displayMessage = message ?? defaultMessage;

  // Determine modal title based on variant
  const getTitle = () => {
    switch (variant) {
      case 'success':
        return labels?.['checkout.modal.savedQuote.title'] ?? 'Saved quote to prospect';
      case 'sending':
        return labels?.['checkout.modal.sendingDocusign.title'] ?? 'Sending docusign';
      case 'sent':
        return labels?.['checkout.modal.docusignSent.title'] ?? 'Docusign sent';
      default:
        return '';
    }
  };

  // Determine modal content based on variant
  const getContent = () => {
    switch (variant) {
      case 'success':
        return (
          <Typography variant="body" size="medium" theme={theme} color="white" align="center">
            {displayMessage}
          </Typography>
        );
      case 'sending':
        return (
          <>
            <Typography variant="body" size="medium" theme={theme} color="white" align="center">
              {labels?.['checkout.modal.sendingDocusign.message'] ??
                'Please wait, sending Docusign to customer.'}
            </Typography>
            {showLoader && (
              <Box className={styles.loaderContainer}>
                <Loader size={32} theme={theme} />
              </Box>
            )}
          </>
        );
      case 'sent':
        return (
          <>
            <Typography variant="body" size="medium" align="center" theme={theme} color="white">
              {labels?.['checkout.modal.docusignSent.message'] ??
                'Docusign has been successfully sent to customer.'}
            </Typography>
          </>
        );
      default:
        return null;
    }
  };

  // Prevent closing during sending state
  const closeOnOverlayClick = variant !== 'sending';
  const closeOnEscape = variant !== 'sending';

  return (
    <LegoModal
      dataTestId={dataTestId}
      isOpen={isOpen}
      onClose={onClose}
      title={getTitle()}
      theme={theme}
      size="small"
      closeOnOverlayClick={closeOnOverlayClick}
      closeOnEscape={closeOnEscape}
      footer={
        <Box className={styles.footer}>
          <Button
            label={labels?.['checkout.modal.close'] ?? 'CLOSE'}
            type="primary"
            color="neutral"
            theme={theme}
            size="medium"
            handleClick={onClose}
            dataTestId={`${dataTestId}-close-button`}
            gaComponent={`${variant}-modal-close`}
            isFluid={variant !== 'success'}
          />
        </Box>
      }
    >
      <Box className={styles.content}>{getContent()}</Box>
    </LegoModal>
  );
};

CheckoutModal.displayName = 'CheckoutModal';

export default CheckoutModal;
